// @LTG-ID: data/journals/personal-readiness-to-exit
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 21 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Personal Readiness (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "personal-readiness-to-exit",
    FRONTMATTER: {
        number: "58",
        section: "Personal Readiness",
        sectionHref: "/journals#personal-readiness",
        sectionAnchor: "personal-readiness",
        title: "Are You Personally Ready to Exit? The Owner's Guide to Financial and Life Readiness",
        deck: "Personal exit readiness has two parts: financial readiness, meaning the sale proceeds plus existing assets can fund your next chapter, and life readiness, meaning you know what you are exiting to.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-29",
        publishedHuman: "May 29, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 4,
        wordCount: 701,
        timeRequired: "PT4M",
        keywords: "am I ready to sell my business, personal readiness exit planning, retiring from business ownership, business owner retirement readiness",
        articleSection: "Personal Readiness",
        articleBodyPreview: "Personal exit readiness has two parts: financial readiness, meaning the sale proceeds plus existing assets can fund your next chapter, and life readiness, meaning you know what you are exiting to. Research shows most seller regret stems from personal unpreparedness, not deal terms, which is why both dimensions deserve planning. The business can be perfectly prepared while the owner is not. In fact, the research suggests that mismatch is the normal case, and it is where most of the pain in exits ",
        complianceNote: "This article is educational and does not constitute financial, tax, or investment advice. Consult qualified professionals about your specific situation.",
        needsDisclaimerFooter: true,
        internalLinks: [
        "exit-planning-guide",
        "what-percentage-of-businesses-sell",
        "walk-away-number-selling-business",
        "life-after-selling-business",
        "family-succession-vs-selling"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Are You Personally Ready to Exit? Financial & Life Readiness",
        canonical: "https://letstalkgrowth.com/journals/personal-readiness-to-exit",
        ogImage: "/journals/og/personal-readiness-to-exit.jpg",
    },
    BODY_MARKDOWN: `Personal exit readiness has two parts: financial readiness, meaning the sale proceeds plus existing assets can fund your next chapter, and life readiness, meaning you know what you are exiting to. Research shows most seller regret stems from personal unpreparedness, not deal terms, which is why both dimensions deserve planning.

The business can be perfectly prepared while the owner is not. In fact, the research suggests that mismatch is the normal case, and it is where most of the pain in exits actually comes from.

## The finding that should reorganize your planning

The Exit Planning Institute's research on owner readiness has repeatedly reported two findings that belong side by side. First, the large majority of an average owner's net worth is concentrated in the business itself. Second, a substantial share of owners who sell report significant regret within about a year, and the regret is rarely about price. It is about waking up without a place to go, a team to lead, or a reason the phone rings. Financial planning gets all the attention; life planning causes most of the casualties.

Personal readiness therefore has two questions, and they are different questions.

## Question one: does the math work?

Financial readiness means that everything you will walk away with, after-tax sale proceeds, existing savings and investments, real estate, and any continuing income, can sustainably fund the life you intend. Working through it honestly involves a chain of steps:

- **Start with the life, not the number.** Annual spending in your next chapter, including the items the business quietly paid for: vehicle, phone, travel, health insurance. Owners are routinely surprised by how much of their lifestyle lived inside the company.
- **Estimate net, not gross.** A headline price shreds down through debt payoff, transaction costs, and taxes before it reaches you. Deal structure changes the tax bite materially, which is why the structure conversation in our asset versus stock sale article matters to your retirement, not just your negotiation.
- **Convert assets to income.** Financial planners commonly frame sustainable retirement income as a modest annual withdrawal percentage from invested assets, adjusted for the length of your horizon; the widely discussed research on safe withdrawal rates, from Bengen's original work to Morningstar's ongoing updates, is the standard reference point. A planner can tailor the rate to your horizon and risk tolerance.
- **Find the gap, then plan against it.** If required assets exceed likely proceeds plus savings, you have a gap, and a gap is not a verdict. It is a planning input: grow the business's value, work longer, adjust the plan, or structure a deal with continuing income.

Knowing this number before going to market changes everything about how you negotiate. An owner with a known walk-away number can decline a weak offer calmly. An owner without one negotiates against their own anxiety.

## Question two: what are you exiting to?

The second dimension resists spreadsheets, which is why it gets skipped. It should not be. The identity questions are predictable: Who are you when you are not the owner? What replaces the structure, status, and problem-solving that filled your weeks? What does your spouse expect your presence to look like, and have you actually discussed it?

The owners who report satisfaction after selling tend to share a trait: they were running toward something, a venture, a role, family, service, craft, not merely away from fatigue. Burnout is a reason to fix the business or reduce your role in it; on its own, it is a poor reason to sell, because the fatigue leaves and the void remains. Our life-after-sale article goes deeper on this.

## Reading your own position

Put the two dimensions together and every owner lands somewhere useful:

- **Financially and personally ready:** you have the rare luxury of choosing timing, which is itself worth money; sell into strength.
- **Financially ready, personally not:** slow down. Deals signed to escape rather than arrive produce the regret statistics. Build the next chapter first.
- **Personally ready, financially short:** the gap defines your work plan, and the value-building series in this Journal is effectively your syllabus.
- **Neither yet:** ideal, honestly, because time is the most valuable input to both dimensions, and you still have it.`,
    FAQ: [
        { question: "How do I know if I am financially ready to sell my business?", answer: "Compare your expected after-tax, after-debt proceeds plus existing assets against the capital needed to fund your intended annual spending over your horizon. A financial planner and your CPA can pressure-test the assumptions; the key is doing the math before going to market, not after an offer arrives." },
        { question: "Why do so many owners regret selling?", answer: "Research from the Exit Planning Institute and others consistently points to personal unpreparedness: loss of identity, structure, and purpose. Regret correlates far more with having no post-sale plan than with the financial terms of the deal." },
        { question: "Should I sell if I am burned out?", answer: "Burnout is a signal to change your role, reduce owner dependence, or fix what is draining you, not necessarily to sell. Owners who sell purely to escape often carry the dissatisfaction with them. If a sale is still right, build the next chapter before closing, not after." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
            meta: "4 min · Brief 39",
            to: "/journals/exit-planning-guide",
        },
        {
            tag: "Exit Planning",
            title: "What Percentage of Businesses Actually Sell? The Statistics Every Owner Should See",
            meta: "3 min · Brief 44",
            to: "/journals/what-percentage-of-businesses-sell",
        },
        {
            tag: "Personal Readiness",
            title: "What Is Your Walk-Away Number? Calculating How Much You Need From the Sale",
            meta: "3 min · Brief 59",
            to: "/journals/walk-away-number-selling-business",
        },
    ],
};

export default BRIEF;
